import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { useLazyQuery, ApolloClient } from "@apollo/client";
import type { DocumentNode } from "graphql";
import { Button, Modal, Box, Typography } from "@mui/material";
import autoGradeClient from "utils/graphql/autoGradeClient";

interface UploadBoxProps {
  setUserAnswerAsPdf: (url: string) => void;
  filename: string;
  setFilename: (name: string) => void;
  usecase: string;
  disabled?: boolean;
  acceptedFormats?: string[];
  presignedUrlQuery: DocumentNode;
  client: ApolloClient<any>;
  placeholderText?: string;
}

const UploadBox = ({
  setUserAnswerAsPdf,
  filename,
  setFilename,
  usecase,
  disabled = false,
  acceptedFormats = ["pdf"],
  presignedUrlQuery,
  client,
  placeholderText,
}: UploadBoxProps) => {
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [getPresignedUrl] = useLazyQuery(presignedUrlQuery, {
    client,
    fetchPolicy: "no-cache",
  });

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileType = file.type;
      const fileName = file.name;
      setLoading(true);

      try {
        // 1. Presigned URL
        const { data } = await getPresignedUrl({
          variables: {
            usecase: usecase,
            objectName: fileName,
            expiration: 3600,
          },
          client: client,
        });

        console.log('data',data)
        const presignedUrl = data?.getPresignedUrl;
        if (!presignedUrl)
          throw new Error("Server did not return a presigned URL.");

        // 2. Upload to S3
        await axios.put(presignedUrl, file, {
          headers: { "Content-Type": fileType },
        });

        // 3. Update parent state
        setFilename(fileName);
        setUserAnswerAsPdf(presignedUrl.split("?")[0]);
      } catch (error: any) {
        console.error("Upload error:", error);
        setErrorMessage(
          error?.message ??
            "An unexpected error occurred during the upload. Please try again."
        );
        setErrorOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [getPresignedUrl, setFilename, setUserAnswerAsPdf, usecase]
  );

  /** Clear local & parent state so the same file can be selected again */
  const handleRemove = () => {
    setFilename("");
    setUserAnswerAsPdf("");
    // reset the native file input so onChange will fire for the same file name
    if (inputRef.current) inputRef.current.value = "";
  };

  const getAcceptString = () =>
    acceptedFormats.map((ext) => `.${ext}`).join(",");

  const disabledOrLoading = disabled || loading;

  return (
    <div className="file-upload-view">
      <label
        style={{
          display: "block",
          cursor: disabledOrLoading ? "not-allowed" : "pointer",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={getAcceptString()}
          onChange={handleFileChange}
          disabled={disabledOrLoading}
          style={{ display: "none" }}
        />

        <div
          style={{
            border: "1px dashed #ccc",
            padding: "10px",
            borderRadius: 4,
            textAlign: "center",
            opacity: disabledOrLoading ? 0.6 : 1,
          }}
        >
          {loading ? (
            "Uploading..."
          ) : filename ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                File uploaded: <strong>{filename}</strong>
              </span>
              <Button
                size="small"
                onClick={handleRemove}
                disabled={disabledOrLoading}
              >
                Remove
              </Button>
            </div>
          ) : (
            `${placeholderText} (${acceptedFormats.join(", ").toUpperCase()})`
          )}
        </div>
      </label>
      <Modal open={errorOpen} onClose={() => setErrorOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
            width: 360,
            maxWidth: "90%",
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Upload Failed
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setErrorOpen(false)} autoFocus>
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default UploadBox;
